import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

/**
 * NoteModal — v2 visual equivalent of v1's inline-styled modal.
 *
 * Replaces the v1 hand-rolled overlay (no z-index, no portal) with an MUI Dialog so it
 * always renders above the table / toasts and stacks correctly. Content is identical to
 * v1: Consultation ID, Type, optional "Evaluation form completed by" line, Created,
 * Updated, and the note body in a scrollable box.
 */

const noteContentSx = {
  maxHeight: 360,
  overflowY: "auto",
  whiteSpace: "pre-wrap",
  border: "1px solid #ddd",
  borderRadius: 1,
  p: 1.5,
  mt: 1,
  bgcolor: "#fafafa",
  fontFamily:
    '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace, "Inter", "Segoe UI", sans-serif',
  fontSize: 14,
  lineHeight: 1.6,
};

const NoteModal = ({ isOpen, onClose, note }) => {
  if (!note) return null;

  const isEvaluationForm =
    note.typeRaw === "evaluationform" ||
    (typeof note.type === "string" &&
      note.type.toLowerCase().includes("evaluation form"));

  const evaluationRoleLabel =
    note.evaluationFormCompletedBy === "psychiatrist"
      ? "Psychiatrist"
      : note.evaluationFormCompletedBy === "psychologist"
        ? "Psychologist"
        : null;

  return (
    <Dialog open={Boolean(isOpen)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Note Details
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Consultation ID: {note.id}
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2">
            <strong>Type:</strong> {note.type}
          </Typography>
        </Box>

        {isEvaluationForm && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Evaluation form completed by:</strong>{" "}
              {evaluationRoleLabel ??
                "Unknown (doctor ID not matched to psychiatrist/psychologist lists)"}
            </Typography>
          </Box>
        )}

        <Box sx={{ mb: 1 }}>
          <Typography variant="body2">
            <strong>Created:</strong> {note.created}
          </Typography>
        </Box>

        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2">
            <strong>Updated:</strong> {note.updated}
          </Typography>
        </Box>

        <Box>
          <Typography variant="body2">
            <strong>Note:</strong>
          </Typography>
          <Box sx={noteContentSx}>{note.note}</Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NoteModal;
